/**
 * 统计分析联动状态管理
 * 
 * @description 管理多图表/多表格之间的联动筛选状态
 * @version v1.0.0
 * @date 2025-12-13
 * @module M9.2 联动功能
 * 
 * 功能：
 * - 存储当前激活的联动筛选条件
 * - 提供添加/移除/清空筛选的方法
 * - 计算筛选参数供 API 调用使用
 * - 支持多维度筛选叠加
 * 
 * 使用场景：
 * - 点击企业饼图 → 趋势图/排行榜自动过滤该企业数据
 * - 点击餐段分布 → 其他图表联动显示该餐段数据
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// ============================================================
// 类型定义
// ============================================================

/** 联动维度类型 */
export type LinkageDimension = 
  | 'enterprise'      // 企业
  | 'merchant'        // 商户
  | 'restaurant'      // 餐厅
  | 'meal_segment'    // 餐段
  | 'workplace'       // 职场
  | 'department'      // 部门
  | 'city'            // 城市
  | 'status'          // 状态
  | 'date'            // 日期

/** 联动筛选项 */
export interface LinkageFilter {
  /** 维度标识 */
  dimension: LinkageDimension
  /** 筛选值（ID 或 code） */
  value: string | number | null
  /** 显示标签 */
  label: string
  /** 来源组件 ID */
  sourceId?: string
  /** 创建时间戳（用于排序） */
  timestamp: number
}

/** 联动配置 */
export interface LinkageConfig {
  /** 是否启用联动 */
  enabled: boolean
  /** 允许的联动维度 */
  allowedDimensions: LinkageDimension[]
  /** 最大联动筛选数 */
  maxFilters: number
  /** 是否自动清除同维度旧筛选 */
  autoReplaceSameDimension: boolean
}

/** 联动事件 */
export interface LinkageEvent {
  type: 'add' | 'remove' | 'clear' | 'toggle'
  filter?: LinkageFilter
  dimension?: LinkageDimension
}

// ============================================================
// Store 定义
// ============================================================

export const useAnalyticsLinkageStore = defineStore('analyticsLinkage', () => {
  // ----------------------------------------------------------
  // 状态
  // ----------------------------------------------------------
  
  /** 当前激活的联动筛选 */
  const activeFilters = ref<LinkageFilter[]>([])
  
  /** 联动配置 */
  const config = ref<LinkageConfig>({
    enabled: true,
    allowedDimensions: ['enterprise', 'merchant', 'restaurant', 'meal_segment', 'workplace', 'city', 'status'],
    maxFilters: 5,
    autoReplaceSameDimension: true
  })
  
  /** 联动事件历史（用于调试） */
  const eventHistory = ref<LinkageEvent[]>([])
  
  /** 是否暂停联动（用于批量操作时临时禁用） */
  const isPaused = ref(false)
  
  // ----------------------------------------------------------
  // 计算属性
  // ----------------------------------------------------------
  
  /** 是否有激活的筛选 */
  const hasActiveFilters = computed(() => activeFilters.value.length > 0)
  
  /** 激活筛选数量 */
  const filterCount = computed(() => activeFilters.value.length)
  
  /** 筛选参数（用于 API 调用） */
  const filterParams = computed(() => {
    const params: Record<string, string | number> = {}
    activeFilters.value.forEach(f => {
      if (f.value !== null && f.value !== undefined) {
        // 转换为 API 参数格式
        const paramKey = getParamKey(f.dimension)
        params[paramKey] = f.value
      }
    })
    return params
  })
  
  /** 按维度分组的筛选 */
  const filtersByDimension = computed(() => {
    const grouped: Partial<Record<LinkageDimension, LinkageFilter>> = {}
    activeFilters.value.forEach(f => {
      grouped[f.dimension] = f
    })
    return grouped
  })
  
  /** 筛选摘要文本 */
  const filterSummary = computed(() => {
    if (!hasActiveFilters.value) return ''
    return activeFilters.value.map(f => f.label).join(' · ')
  })
  
  // ----------------------------------------------------------
  // 方法
  // ----------------------------------------------------------
  
  /**
   * 添加联动筛选
   * 
   * @param filter 筛选项
   */
  function addFilter(filter: Omit<LinkageFilter, 'timestamp'>) {
    if (!config.value.enabled || isPaused.value) return
    
    // 检查维度是否允许
    if (!config.value.allowedDimensions.includes(filter.dimension)) {
      console.warn(`[analyticsLinkage] 维度 ${filter.dimension} 不在允许列表中`)
      return
    }
    
    // 创建完整的筛选对象
    const newFilter: LinkageFilter = {
      ...filter,
      timestamp: Date.now()
    }
    
    // 检查是否已存在同维度筛选
    const existingIndex = activeFilters.value.findIndex(
      f => f.dimension === filter.dimension
    )
    
    if (existingIndex >= 0) {
      if (config.value.autoReplaceSameDimension) {
        // 替换同维度筛选
        activeFilters.value[existingIndex] = newFilter
      } else {
        // 不允许重复维度
        console.warn(`[analyticsLinkage] 维度 ${filter.dimension} 已存在筛选`)
        return
      }
    } else {
      // 检查是否超过最大数量
      if (activeFilters.value.length >= config.value.maxFilters) {
        // 移除最旧的筛选
        activeFilters.value.shift()
      }
      activeFilters.value.push(newFilter)
    }
    
    // 记录事件
    recordEvent({ type: 'add', filter: newFilter })
  }
  
  /**
   * 移除指定维度的筛选
   * 
   * @param dimension 维度
   */
  function removeFilter(dimension: LinkageDimension) {
    const index = activeFilters.value.findIndex(f => f.dimension === dimension)
    if (index >= 0) {
      const removed = activeFilters.value.splice(index, 1)[0]
      recordEvent({ type: 'remove', filter: removed, dimension })
    }
  }
  
  /**
   * 切换筛选（存在则移除，不存在则添加）
   * 
   * @param filter 筛选项
   */
  function toggleFilter(filter: Omit<LinkageFilter, 'timestamp'>) {
    const existing = activeFilters.value.find(
      f => f.dimension === filter.dimension && f.value === filter.value
    )
    
    if (existing) {
      removeFilter(filter.dimension)
    } else {
      addFilter(filter)
    }
    
    recordEvent({ type: 'toggle', dimension: filter.dimension })
  }
  
  /**
   * 清空所有筛选
   */
  function clearFilters() {
    activeFilters.value = []
    recordEvent({ type: 'clear' })
  }
  
  /**
   * 检查指定维度是否有筛选
   * 
   * @param dimension 维度
   */
  function hasFilter(dimension: LinkageDimension): boolean {
    return activeFilters.value.some(f => f.dimension === dimension)
  }
  
  /**
   * 获取指定维度的筛选值
   * 
   * @param dimension 维度
   */
  function getFilterValue(dimension: LinkageDimension): string | number | null {
    const filter = activeFilters.value.find(f => f.dimension === dimension)
    return filter?.value ?? null
  }
  
  /**
   * 暂停联动
   */
  function pause() {
    isPaused.value = true
  }
  
  /**
   * 恢复联动
   */
  function resume() {
    isPaused.value = false
  }
  
  /**
   * 更新配置
   * 
   * @param newConfig 新配置
   */
  function updateConfig(newConfig: Partial<LinkageConfig>) {
    config.value = { ...config.value, ...newConfig }
  }
  
  /**
   * 重置 Store
   */
  function $reset() {
    activeFilters.value = []
    eventHistory.value = []
    isPaused.value = false
  }
  
  // ----------------------------------------------------------
  // 内部方法
  // ----------------------------------------------------------
  
  /**
   * 获取 API 参数键名
   */
  function getParamKey(dimension: LinkageDimension): string {
    const keyMap: Record<LinkageDimension, string> = {
      enterprise: 'enterprise_id',
      merchant: 'merchant_id',
      restaurant: 'restaurant_id',
      meal_segment: 'meal_segment',
      workplace: 'workplace_id',
      department: 'department_id',
      city: 'city',
      status: 'status',
      date: 'date'
    }
    return keyMap[dimension] || dimension
  }
  
  /**
   * 记录事件
   */
  function recordEvent(event: LinkageEvent) {
    eventHistory.value.push(event)
    // 只保留最近 50 条
    if (eventHistory.value.length > 50) {
      eventHistory.value.shift()
    }
  }
  
  // ----------------------------------------------------------
  // 返回
  // ----------------------------------------------------------
  
  return {
    // 状态
    activeFilters,
    config,
    eventHistory,
    isPaused,
    
    // 计算属性
    hasActiveFilters,
    filterCount,
    filterParams,
    filtersByDimension,
    filterSummary,
    
    // 方法
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    hasFilter,
    getFilterValue,
    pause,
    resume,
    updateConfig,
    $reset
  }
})

// ============================================================
// 维度标签映射
// ============================================================

/** 维度中文标签 */
export const LINKAGE_DIMENSION_LABELS: Record<LinkageDimension, string> = {
  enterprise: '企业',
  merchant: '商户',
  restaurant: '餐厅',
  meal_segment: '餐段',
  workplace: '职场',
  department: '部门',
  city: '城市',
  status: '状态',
  date: '日期'
}

/**
 * 获取维度中文标签
 */
export function getLinkageDimensionLabel(dimension: LinkageDimension): string {
  return LINKAGE_DIMENSION_LABELS[dimension] || dimension
}

